import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Language, Message, AppStatus, User, ChatSession } from './types';
import { UI_STRINGS } from './constants';
import { geminiService } from './services/geminiService';
import { useLiveAssistant } from './hooks/useLiveAssistant';
import { MessageBubble } from './components/MessageBubble';
import { StatusIndicator } from './components/StatusIndicator';
import { VoiceVisualizer } from './components/VoiceVisualizer';
import { 
  Menu, 
  Plus, 
  Trash2, 
  LogOut, 
  MessageSquare, 
  History, 
  X, 
  Check, 
  Globe 
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

// Storage Helper functions
const loadSessions = (username: string): ChatSession[] => {
  try {
    const raw = localStorage.getItem(`baba_chat_sessions_${username}`);
    if (raw) {
      const parsed: ChatSession[] = JSON.parse(raw);
      return parsed.map(session => ({
        ...session,
        messages: session.messages.map(msg => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }))
      }));
    }
  } catch (e) {
    console.error("Failed to load sessions:", e);
  }
  return [];
};

const saveSessionsToStorage = (username: string, sessionsList: ChatSession[]) => {
  try {
    localStorage.setItem(`baba_chat_sessions_${username}`, JSON.stringify(sessionsList));
  } catch (e) {
    console.error("Failed to save sessions:", e);
  }
};

const formatSessionDate = (timestamp: number, lang: Language): string => {
  const date = new Date(timestamp);
  return date.toLocaleDateString(lang === Language.EN_GB ? 'en-GB' : 'pt-PT', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const Logo: React.FC<{ size?: 'sm' | 'lg' }> = ({ size = 'sm' }) => {
  const isLarge = size === 'lg';
  return (
    <div className={`flex flex-col items-center justify-center ${isLarge ? 'space-y-3 sm:space-y-6' : 'flex-row space-x-2 sm:space-x-3 space-y-0'}`}>
      <div className={`relative group ${isLarge ? 'w-24 h-24 xs:w-32 xs:h-32 sm:w-56 sm:h-56' : 'w-7 h-7 sm:w-10 sm:h-10'}`}>
        <div className="absolute -inset-4 bg-cyan-500/10 rounded-full blur-2xl opacity-50 group-hover:opacity-100 transition duration-1000"></div>
        <svg viewBox="0 0 100 100" className="relative w-full h-full drop-shadow-[0_0_15px_rgba(34,211,238,0.3)]">
          <defs>
            <linearGradient id="robotBody" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#ffffff" />
              <stop offset="100%" stopColor="#e2e8f0" />
            </linearGradient>
            <linearGradient id="visorBg" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#020617" />
              <stop offset="100%" stopColor="#1e293b" />
            </linearGradient>
          </defs>
          <circle cx="50" cy="50" r="45" fill="url(#robotBody)" stroke="#94a3b8" strokeWidth="0.5" />
          <rect x="22" y="32" width="56" height="36" rx="16" fill="url(#visorBg)" />
          <path d="M36 48 Q42 42 48 48" stroke="#22d3ee" strokeWidth="4" fill="none" strokeLinecap="round" />
          <path d="M52 48 Q58 42 64 48" stroke="#22d3ee" strokeWidth="4" fill="none" strokeLinecap="round" />
          <path d="M45 58 Q50 63 55 58" stroke="#22d3ee" strokeWidth="2.5" fill="none" strokeLinecap="round" />
          <path d="M15 50 A35 35 0 0 1 85 50" stroke="#0f172a" strokeWidth="8" fill="none" />
          <circle cx="15" cy="55" r="11" fill="#00247d" stroke="#0f172a" strokeWidth="2" />
          <path d="M15 44 V66 M4 55 H26" stroke="#cf142b" strokeWidth="3" />
          <path d="M7 47 L23 63 M23 47 L7 63" stroke="white" strokeWidth="1" />
          <circle cx="85" cy="55" r="11" fill="#046a38" stroke="#0f172a" strokeWidth="2" />
          <rect x="85" y="44" width="11" height="22" fill="#cf142b" />
          <path d="M85 65 Q75 82 58 78" stroke="#0f172a" strokeWidth="4" fill="none" strokeLinecap="round" />
          <circle cx="58" cy="78" r="4" fill="#020617" />
        </svg>
      </div>
      <div className={`flex flex-col ${isLarge ? 'items-center' : 'items-start'}`}>
        <h1 className={`font-black tracking-tighter bg-clip-text text-transparent bg-gradient-to-b from-white to-slate-400 ${isLarge ? 'text-4xl xs:text-5xl sm:text-7xl mb-0.5 sm:mb-1' : 'text-base sm:text-xl leading-none'}`}>
          BABA
        </h1>
        <p className={`text-cyan-400 font-black tracking-[0.2em] sm:tracking-[0.3em] uppercase ${isLarge ? 'text-[10px] sm:text-base' : 'text-[6px] sm:text-[8px]'}`}>
          Voice Assistant
        </p>
      </div>
    </div>
  );
};

const Login: React.FC<{ onLogin: (user: User) => void; language: Language }> = ({ onLogin, language }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState(false);
  const strings = UI_STRINGS[language];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (code === '1993') {
      onLogin({ name: 'Abreu', type: 'admin' });
    } else if (code === '4321') {
      onLogin({ name: 'Guest', type: 'guest' });
    } else {
      setError(true);
      setTimeout(() => setError(false), 2000);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[100dvh] px-6 bg-slate-950 text-white relative overflow-hidden">
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-cyan-500/10 blur-[120px] rounded-full"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-blue-600/10 blur-[120px] rounded-full"></div>
      
      <div className="w-full max-w-sm space-y-10 animate-in fade-in zoom-in-95 duration-700 relative z-10">
        <Logo size="lg" />
        
        <div className="glass p-8 sm:p-10 rounded-[2.5rem] border border-white/10 shadow-2xl space-y-8">
          <div className="text-center space-y-2">
            <h2 className="text-2xl font-black tracking-tight">{strings.loginTitle}</h2>
            <p className="text-slate-400 text-sm font-medium">{strings.loginSubtitle}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 ml-1">{strings.accessCode}</label>
              <input
                type="password"
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="••••"
                className={`w-full bg-white/5 border ${error ? 'border-red-500 ring-4 ring-red-500/10' : 'border-white/10 focus:border-cyan-500/50 focus:ring-4 focus:ring-cyan-500/10'} rounded-2xl px-6 py-4 text-center text-2xl tracking-[1em] font-black outline-none transition-all`}
              />
            </div>
            
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 py-4 rounded-2xl font-black uppercase tracking-widest text-sm shadow-xl shadow-cyan-500/20 active:scale-[0.98] transition-all"
            >
              {strings.loginBtn}
            </button>
          </form>

          {error && (
            <p className="text-red-400 text-center text-xs font-black uppercase tracking-widest animate-bounce">
              {strings.invalidCode}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  // Try loading initial user directly for native persistent session
  const [user, setUser] = useState<User | null>(() => {
    try {
      const stored = localStorage.getItem('baba_active_user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const [language, setLanguage] = useState<Language>(Language.EN_GB);
  const [messages, setMessages] = useState<Message[]>([]);
  const [status, setStatus] = useState<AppStatus>(AppStatus.IDLE);
  const [error, setError] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('');
  
  // Chat History Management States
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  
  const scrollRef = useRef<HTMLDivElement>(null);

  // Initialize and load sessions when user logs in
  useEffect(() => {
    if (user) {
      const loaded = loadSessions(user.name);
      if (loaded.length > 0) {
        setSessions(loaded);
        const activeId = loaded[0].id;
        setActiveSessionId(activeId);
        setMessages(loaded[0].messages);
        setLanguage(loaded[0].language);
      } else {
        // Create an initial empty session
        const initialId = Math.random().toString(36).substr(2, 9);
        const initialSession: ChatSession = {
          id: initialId,
          title: language === Language.EN_GB ? 'New Chat' : 'Nova conversa',
          messages: [],
          language: language,
          updatedAt: Date.now()
        };
        setSessions([initialSession]);
        setActiveSessionId(initialId);
        setMessages([]);
      }
    } else {
      setSessions([]);
      setActiveSessionId(null);
      setMessages([]);
    }
  }, [user]);

  // Sync messages and language to active session state and local storage
  useEffect(() => {
    if (!user || !activeSessionId) return;

    setSessions(prev => {
      const sessionIndex = prev.findIndex(s => s.id === activeSessionId);
      if (sessionIndex === -1) return prev;

      const currentSession = prev[sessionIndex];
      const messagesChanged = JSON.stringify(currentSession.messages) !== JSON.stringify(messages);
      const langChanged = currentSession.language !== language;

      if (!messagesChanged && !langChanged) return prev;

      let title = currentSession.title;
      // Extract first 25 chars from first user request as dynamic chat title
      if ((title === 'New Chat' || title === 'Nova conversa') && messages.length > 0) {
        const firstUserMsg = messages.find(m => m.role === 'user');
        if (firstUserMsg) {
          title = firstUserMsg.text.length > 25 
            ? firstUserMsg.text.substring(0, 25) + '...' 
            : firstUserMsg.text;
        }
      }

      const updatedSession: ChatSession = {
        ...currentSession,
        messages,
        language,
        title,
        updatedAt: Date.now()
      };

      const updatedSessions = [...prev];
      updatedSessions[sessionIndex] = updatedSession;
      
      const sorted = updatedSessions.sort((a, b) => b.updatedAt - a.updatedAt);
      saveSessionsToStorage(user.name, sorted);
      return sorted;
    });
  }, [messages, language, activeSessionId, user]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, status]);

  const addMessage = useCallback((role: 'user' | 'assistant', text: string) => {
    if (!text) return;
    const newMessage: Message = {
      id: Math.random().toString(36).substr(2, 9),
      role,
      text,
      timestamp: new Date()
    };
    setMessages(prev => {
      const lastMsg = prev[prev.length - 1];
      if (lastMsg && lastMsg.role === role && lastMsg.text === text) return prev;
      return [...prev, newMessage];
    });
  }, []);

  // Effect to handle guest greeting upon login
  useEffect(() => {
    if (user?.type === 'guest' && messages.length === 0) {
      setTimeout(() => {
        addMessage('assistant', UI_STRINGS[language].guestGreeting);
      }, 1000);
    }
  }, [user, language, addMessage, messages.length]);

  const handleError = useCallback((errMsg: string) => {
    setError(errMsg);
    setStatus(AppStatus.ERROR);
    const isApiKeyError = errMsg.toUpperCase().includes("API KEY");
    const duration = isApiKeyError ? 25000 : 8000;
    setTimeout(() => {
      setError(null);
      setStatus(current => current === AppStatus.ERROR ? AppStatus.IDLE : current);
    }, duration);
  }, []);

  const handleAssistantResponse = async (userInput: string) => {
    if (!userInput.trim() || status === AppStatus.PROCESSING) return;
    
    addMessage('user', userInput);
    setStatus(AppStatus.PROCESSING);
    setInputValue('');
    
    try {
      const response = await geminiService.generateResponse(userInput, messages, language, user?.name || 'User');
      addMessage('assistant', response);
      setStatus(AppStatus.IDLE);
    } catch (err: any) {
      handleError(err?.message || UI_STRINGS[language].errorGeneral);
    }
  };

  const { isActive, isSpeaking, startSession, stopSession, micLevel, speakerLevel } = useLiveAssistant({
    language,
    history: messages,
    userName: user?.name || 'User',
    onMessageReceived: addMessage,
    onError: handleError
  });

  useEffect(() => {
    if (isActive) {
      if (isSpeaking) setStatus(AppStatus.SPEAKING);
      else setStatus(AppStatus.LISTENING);
    } else {
      if (status !== AppStatus.PROCESSING && status !== AppStatus.ERROR) {
        setStatus(AppStatus.IDLE);
      }
    }
  }, [isActive, isSpeaking, status]);

  const toggleLanguage = () => {
    const nextLang = language === Language.EN_GB ? Language.PT_PT : Language.EN_GB;
    setLanguage(nextLang);
    if (isActive) stopSession();
    setStatus(AppStatus.IDLE);
  };

  const clearHistory = () => {
    setMessages([]);
    if (isActive) stopSession();
    setStatus(AppStatus.IDLE);
    setError(null);
  };

  const handleTextSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim() && status !== AppStatus.PROCESSING && !isActive) {
      handleAssistantResponse(inputValue);
    }
  };

  const handleLogin = (newUser: User) => {
    setUser(newUser);
    try {
      localStorage.setItem('baba_active_user', JSON.stringify(newUser));
    } catch (e) {
      console.error(e);
    }
  };

  const handleLogout = () => {
    if (isActive) stopSession();
    setUser(null);
    setSessions([]);
    setActiveSessionId(null);
    setMessages([]);
    setError(null);
    setStatus(AppStatus.IDLE);
    try {
      localStorage.removeItem('baba_active_user');
    } catch (e) {
      console.error(e);
    }
  };

  const handleSelectSession = (id: string) => {
    const session = sessions.find(s => s.id === id);
    if (session) {
      if (isActive) stopSession();
      setActiveSessionId(id);
      setMessages(session.messages);
      setLanguage(session.language);
      setStatus(AppStatus.IDLE);
      setError(null);
      setIsSidebarOpen(false); // Auto-close on mobile selection
    }
  };

  const handleNewChat = () => {
    if (isActive) stopSession();
    const newId = Math.random().toString(36).substr(2, 9);
    const newSession: ChatSession = {
      id: newId,
      title: language === Language.EN_GB ? 'New Chat' : 'Nova conversa',
      messages: [],
      language: language,
      updatedAt: Date.now()
    };
    setSessions(prev => [newSession, ...prev]);
    setActiveSessionId(newId);
    setMessages([]);
    setStatus(AppStatus.IDLE);
    setError(null);
    setIsSidebarOpen(false);
  };

  const handleDeleteSession = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (id === activeSessionId && isActive) {
      stopSession();
    }

    const updatedSessions = sessions.filter(s => s.id !== id);
    setSessions(updatedSessions);
    saveSessionsToStorage(user!.name, updatedSessions);

    if (id === activeSessionId) {
      if (updatedSessions.length > 0) {
        handleSelectSession(updatedSessions[0].id);
      } else {
        const newId = Math.random().toString(36).substr(2, 9);
        const newSession: ChatSession = {
          id: newId,
          title: language === Language.EN_GB ? 'New Chat' : 'Nova conversa',
          messages: [],
          language: language,
          updatedAt: Date.now()
        };
        setSessions([newSession]);
        setActiveSessionId(newId);
        setMessages([]);
      }
    }
  };

  if (!user) {
    return <Login language={language} onLogin={handleLogin} />;
  }

  const strings = UI_STRINGS[language];

  return (
    <div className="flex h-[100dvh] w-full bg-slate-950 overflow-hidden relative font-sans text-slate-100 selection:bg-cyan-500/30">
      {/* Dynamic Ambient Background Elements */}
      <div className="fixed -top-40 -left-40 w-[250px] h-[250px] sm:w-[600px] sm:h-[600px] bg-cyan-600/10 rounded-full blur-[80px] sm:blur-[120px] pointer-events-none z-0"></div>
      <div className="fixed -bottom-40 -right-40 w-[250px] h-[250px] sm:w-[600px] sm:h-[600px] bg-blue-900/20 rounded-full blur-[80px] sm:blur-[120px] pointer-events-none z-0"></div>

      {/* Modern Side Navigation / Chat History Panel */}
      <AnimatePresence>
        {isSidebarOpen && (
          <>
            {/* Dark blur backdrop for mobile screens */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsSidebarOpen(false)}
              className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 md:hidden"
            />

            {/* Sidebar content drawer */}
            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 220 }}
              className="fixed md:static inset-y-0 left-0 w-80 max-w-[85vw] bg-slate-950/95 md:bg-slate-950/45 backdrop-blur-3xl border-r border-white/5 z-50 md:z-30 flex flex-col h-full pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]"
            >
              {/* Sidebar Header */}
              <div className="p-4 sm:p-5 border-b border-white/5 flex items-center justify-between shrink-0">
                <div className="flex items-center space-x-2">
                  <Logo size="sm" />
                </div>
                <button
                  onClick={() => setIsSidebarOpen(false)}
                  className="p-1.5 hover:bg-white/5 rounded-xl text-slate-400 hover:text-white transition-colors"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Start a New Conversation */}
              <div className="p-4 shrink-0">
                <button
                  onClick={handleNewChat}
                  className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 hover:from-cyan-500/20 hover:to-blue-500/20 border border-cyan-500/30 hover:border-cyan-500/50 py-3.5 rounded-2xl font-black uppercase tracking-wider text-[11px] text-cyan-400 hover:shadow-lg hover:shadow-cyan-500/5 transition-all active:scale-[0.98]"
                >
                  <Plus size={16} />
                  <span>{strings.newChat}</span>
                </button>
              </div>

              {/* Chat Session Item Grid */}
              <div className="flex-1 overflow-y-auto px-2 pb-4 space-y-1 no-scrollbar">
                {sessions.length === 0 ? (
                  <div className="text-center py-10 text-xs text-slate-500 font-black uppercase tracking-widest">
                    {strings.noChats}
                  </div>
                ) : (
                  sessions.map((session) => {
                    const isSelected = session.id === activeSessionId;
                    return (
                      <div
                        key={session.id}
                        onClick={() => handleSelectSession(session.id)}
                        className={`group relative flex items-center justify-between p-3.5 rounded-2xl cursor-pointer transition-all border ${
                          isSelected
                            ? 'bg-cyan-500/10 border-cyan-500/20 text-cyan-200 shadow-[inset_0_1px_1px_rgba(255,255,255,0.05)]'
                            : 'bg-transparent border-transparent hover:bg-white/5 text-slate-300 hover:text-white'
                        }`}
                      >
                        <div className="flex items-center space-x-3 overflow-hidden pr-2">
                          <MessageSquare size={16} className={isSelected ? 'text-cyan-400 shrink-0' : 'text-slate-500 shrink-0'} />
                          <div className="flex flex-col overflow-hidden text-left">
                            <span className="text-xs font-black truncate max-w-[180px]">
                              {session.title}
                            </span>
                            <span className="text-[9px] text-slate-500 font-medium mt-0.5">
                              {formatSessionDate(session.updatedAt, language)}
                            </span>
                          </div>
                        </div>

                        {/* Interactive Trash Control */}
                        <button
                          onClick={(e) => handleDeleteSession(session.id, e)}
                          title={strings.deleteChat}
                          className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-red-500/10 text-slate-500 hover:text-red-400 rounded-lg transition-all shrink-0"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Authenticated Member Control footer */}
              <div className="p-4 border-t border-white/5 bg-slate-950/40 space-y-3 shrink-0">
                <div className="flex items-center justify-between px-2">
                  <div className="flex flex-col">
                    <span className="text-xs font-black text-white">{user.name}</span>
                    <span className="text-[9px] font-bold text-cyan-400 uppercase tracking-widest mt-0.5">
                      {user.type === 'admin' ? 'Administrator' : 'Guest'}
                    </span>
                  </div>
                  
                  {/* Logout Button */}
                  <button
                    onClick={handleLogout}
                    title={strings.logout}
                    className="flex items-center justify-center p-2.5 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 hover:border-red-500/40 rounded-xl text-red-400 transition-all active:scale-[0.96]"
                  >
                    <LogOut size={16} />
                  </button>
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Workspace Frame */}
      <div className="flex-1 flex flex-col h-full overflow-hidden relative z-10">
        {/* Main Application Header */}
        <header className="flex items-center justify-between px-4 sm:px-6 pt-[calc(env(safe-area-inset-top)+0.6rem)] pb-2.5 sm:py-4 bg-slate-950/50 backdrop-blur-2xl border-b border-white/5 z-20 shrink-0">
          <div className="flex items-center space-x-3">
            {/* Collapsible history activator trigger */}
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="p-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-slate-300 hover:text-cyan-400 transition-all active:scale-[0.95]"
              title={strings.chatHistory}
            >
              <History size={16} />
            </button>
            <Logo />
          </div>

          <div className="flex items-center space-x-2 sm:space-x-4">
            <button 
              onClick={clearHistory} 
              className="text-[9px] sm:text-[10px] font-black text-slate-500 hover:text-cyan-400 transition-colors px-2 py-1 uppercase tracking-[0.2em]"
            >
              {strings.clearChat}
            </button>
            <button 
              onClick={toggleLanguage} 
              className="flex items-center space-x-1.5 sm:space-x-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-white/5 hover:bg-white/10 rounded-xl transition-all border border-white/10 shadow-lg"
            >
              <span className="hidden sm:inline text-[10px] font-bold text-slate-300 uppercase tracking-widest">{strings.switchLang}</span>
              <span className="text-base sm:text-xl leading-none">{language === Language.EN_GB ? '🇵🇹' : '🇬🇧'}</span>
            </button>
          </div>
        </header>

        {/* Action Status Ribbon */}
        <StatusIndicator status={status} language={language} />

        {/* Scrollable Conversation Stream */}
        <main ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 sm:p-6 md:p-8 space-y-4 sm:space-y-6 no-scrollbar scroll-smooth relative z-10">
          {messages.length === 0 && user.type === 'admin' && (
            <div className="flex flex-col items-center justify-center min-h-[40vh] sm:min-h-[60vh] text-center animate-in fade-in duration-1000 space-y-4 sm:space-y-10 py-10">
              <div className="scale-90 xs:scale-100 sm:scale-110 hover:scale-115 transition-transform duration-700">
                <Logo size="lg" />
              </div>
              <div className="max-w-md space-y-2 sm:space-y-4 px-4 sm:px-6">
                <h2 className="text-xl sm:text-3xl font-black text-white tracking-tight leading-tight">{strings.welcome}</h2>
                <p className="text-slate-400 font-medium leading-relaxed text-sm sm:text-lg">
                  {language === Language.EN_GB 
                    ? `Hello ${user.name}, your personal AI companion is ready.` 
                    : `Olá ${user.name}, o teu companheiro IA pessoal está pronto.`}
                </p>
              </div>
            </div>
          )}

          {messages.map((msg) => (
            <MessageBubble key={msg.id} message={msg} />
          ))}
          
          {status === AppStatus.PROCESSING && (
            <div className="flex w-full mb-4 justify-start animate-in fade-in duration-300">
              <div className="bg-slate-900/40 backdrop-blur-xl border border-white/5 rounded-2xl rounded-tl-none px-4 sm:px-6 py-3 sm:py-5 shadow-2xl">
                <div className="flex space-x-2 py-1">
                  <div className="w-1.5 h-1.5 sm:w-2.5 sm:h-2.5 bg-cyan-400 rounded-full animate-bounce"></div>
                  <div className="w-1.5 h-1.5 sm:w-2.5 sm:h-2.5 bg-cyan-400 rounded-full animate-bounce [animation-delay:0.2s]"></div>
                  <div className="w-1.5 h-1.5 sm:w-2.5 sm:h-2.5 bg-cyan-400 rounded-full animate-bounce [animation-delay:0.4s]"></div>
                </div>
              </div>
            </div>
          )}
        </main>

        {/* Input & Live Vocal Overlay footer */}
        <footer className="px-4 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:px-6 sm:py-8 bg-slate-950/80 backdrop-blur-3xl border-t border-white/5 shrink-0 shadow-[0_-20px_60px_rgba(0,0,0,0.6)] relative z-20 animate-in slide-in-from-bottom duration-500">
          <div className="max-w-4xl mx-auto space-y-3 sm:space-y-6">
            {error && (
              <div className="text-[9px] sm:text-[11px] font-black text-red-100 bg-red-900/90 backdrop-blur-md px-3 sm:px-6 py-2.5 sm:py-4 rounded-2xl border border-red-500/40 text-center uppercase tracking-wider animate-in slide-in-from-bottom-4 shadow-xl mx-auto max-w-full">
                <span className="text-red-400 mr-2 font-black">⚠️ ERROR:</span> {error}
              </div>
            )}

            {/* Glowing Organic Audio Bar Frequencies Wave Visualizer */}
            <VoiceVisualizer 
              micLevel={micLevel} 
              speakerLevel={speakerLevel} 
              isSpeaking={isSpeaking} 
              isActive={isActive} 
            />

            <div className="flex items-center space-x-2 sm:space-x-6">
              <div className="relative flex-1 group">
                <form onSubmit={handleTextSubmit}>
                  <div className="absolute inset-0 bg-cyan-400/5 rounded-2xl blur-md group-hover:bg-cyan-400/10 transition duration-500"></div>
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    disabled={status === AppStatus.PROCESSING || isActive}
                    placeholder={isActive ? strings.listening : strings.placeholder}
                    className="relative w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-3.5 sm:px-8 sm:py-5 text-sm sm:text-base font-semibold focus:ring-4 focus:ring-cyan-500/20 focus:bg-white/[0.08] transition-all outline-none text-white shadow-inner placeholder:text-slate-600 disabled:opacity-40"
                  />
                </form>
              </div>

              <button
                onClick={isActive ? stopSession : startSession}
                disabled={status === AppStatus.PROCESSING}
                className={`
                  relative w-12 h-12 xs:w-14 xs:h-14 sm:w-20 sm:h-20 rounded-2xl sm:rounded-3xl flex items-center justify-center shadow-[0_0_40px_-10px_rgba(0,0,0,0.3)] transition-all duration-500 transform active:scale-95 group overflow-hidden shrink-0
                  ${isActive 
                    ? 'bg-red-500 hover:bg-red-600' 
                    : 'bg-gradient-to-br from-cyan-400 via-blue-500 to-indigo-600 hover:shadow-cyan-500/40'
                  }
                `}
              >
                {isActive ? (
                  <svg className="w-5 h-5 xs:w-6 xs:h-6 sm:w-10 sm:h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <rect x="6" y="6" width="12" height="12" rx="1.5" />
                  </svg>
                ) : (
                  <div className="relative z-10">
                    <svg className="w-5 h-5 xs:w-6 xs:h-6 sm:w-10 sm:h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                      <path d="M12 1v11m0 0a4.5 4.5 0 004.5 4.5M12 12a4.5 4.5 0 01-4.5-4.5M8 17a5 5 0 008 0M12 18v5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M19 10v2a7 7 0 01-14 0v-2" strokeLinecap="round" />
                    </svg>
                  </div>
                )}
                {isActive && (
                  <div className="absolute inset-0 pulse-animation -z-10 opacity-60"></div>
                )}
              </button>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default App;
